import { formatCurrency } from '../../shared/format_currency.js';
import { useCheckoutTripServices } from '../form/use_trip_services.js';
import { useTravelerPhotos } from '../form/use_traveler_photos.js';
import type { CheckoutTripCopy } from '../model/checkout_copy.js';
import type {
  CheckoutItemSource,
  CheckoutMoney,
  CheckoutSubmitBlocker,
  CheckoutTravelerRef,
} from '../model/checkout_source.js';
import type { CheckoutScreenComposition } from '../products/checkout_screen.js';
import { CheckoutTripSummary } from '../summary/checkout_trip_summary.js';
import type { CheckoutSubmitNotice } from './checkout_subject.js';
import { CheckoutPricePanel } from './checkout_price_panel.js';
import { useCheckoutEnv } from './env_context.js';

export interface TripSummaryPanelProps {
  items: CheckoutItemSource[];
  /** O contexto, o resumo e os rótulos do produto (`checkoutScreenComposition`). */
  composition: CheckoutScreenComposition;
  /** A viagem deste checkout (#3180): com ela o cartão também lista os serviços
   *  JÁ comprados da viagem, em seção própria e fora da conta do total.
   *  Ausente ⇒ o cartão é só o carrinho, como sempre foi. */
  tripId?: string;
  travelers?: CheckoutTravelerRef[];
  perTraveler?: CheckoutMoney;
  total: CheckoutMoney;
  /** Daily/nightly count for the price rows (rental/hotel); absent otherwise. */
  units?: number;
  submitting: boolean;
  canSubmit: boolean;
  /** Pendências que travam o CTA — repassadas ao `CheckoutPricePanel`. */
  submitBlockers?: CheckoutSubmitBlocker[];
  /** Faixa âmbar entre o CTA e a nota (Figma 614-7416). */
  submitNotice?: CheckoutSubmitNotice;
  onSubmit: () => void;
  /** Saída secundária do rodapé no touch — repassada ao `CheckoutPricePanel`. */
  onBack?: () => void;
}

/** "5 noites" da meta line; o produto sem noites fica só com as datas. */
function nightsLabel(nights: number | undefined, trip: CheckoutTripCopy): string | undefined {
  if (nights === undefined) return undefined;
  return trip.nights(nights);
}

/** O viajante da meta line: o nome, ou "2 viajantes" com o avatar (foto ou
 *  iniciais) do primeiro. */
export function summaryTraveler(
  travelers: readonly CheckoutTravelerRef[],
  photos: Readonly<Record<string, string | null>>,
  trip: CheckoutTripCopy,
) {
  const first = travelers[0];
  // Sem nome não há o que mostrar: nem avatar, nem rótulo, nem o separador.
  if (!first?.name) return undefined;
  return {
    name: first.name,
    photoUrl: photos[first.id] ?? null,
    label: travelers.length === 1 ? first.name : trip.travelers(travelers.length),
  };
}

/** O cartão "Sua viagem" do checkout online: o carrinho no `CheckoutTripSummary`,
 *  com o detalhe de cada produto e o rodapé de preço. */
export function TripSummaryPanel({
  items,
  composition,
  tripId,
  travelers,
  perTraveler,
  total,
  units,
  submitting,
  canSubmit,
  submitBlockers,
  submitNotice,
  onSubmit,
  onBack,
}: Readonly<TripSummaryPanelProps>) {
  const { io, copy, locale } = useCheckoutEnv();
  const { trip } = copy;
  const booked = useCheckoutTripServices(io, tripId);
  const firstId = travelers?.[0]?.id;
  const photos = useTravelerPhotos(io, firstId ? [firstId] : []);
  const ctx = composition.context;
  if (items.length === 0) return null;
  const inPolicy = items.every((item) => item.detail.withinPolicy);
  const labels = items.length > 1 ? composition.lineLabels() : [];
  const priceLines =
    items.length > 1
      ? items.map((item, index) => ({
          id: item.offerId,
          label: labels[index] ?? item.offerId,
          price: item.price,
        }))
      : undefined;
  const traveler = summaryTraveler(travelers ?? [], photos, trip);
  return (
    <CheckoutTripSummary
      kicker={trip.sectionTitle}
      {...(inPolicy ? { policyBadge: trip.inPolicy } : {})}
      {...(ctx.city ? { title: trip.title(ctx.city) } : {})}
      meta={[ctx.dates, nightsLabel(ctx.nights, trip)].filter(Boolean).join(' · ')}
      {...(traveler ? { traveler } : {})}
      booked={{
        label: trip.alreadyBooked,
        services: booked.map((service) => ({
          id: service.id,
          title: service.title,
          ...(service.statusLabel ? { status: service.statusLabel } : {}),
          ...(service.price
            ? { price: formatCurrency(service.price.amount, service.price.currency, locale) }
            : {}),
        })),
      }}
      beingRequestedLabel={trip.beingRequested}
      footer={
        <CheckoutPricePanel
          days={units}
          items={priceLines}
          perTraveler={perTraveler}
          total={total}
          noun={ctx.noun}
          {...(submitNotice ? { submitNotice } : {})}
          onSubmit={onSubmit}
          {...(onBack ? { onBack } : {})}
          submitting={submitting}
          canSubmit={canSubmit}
          {...(submitBlockers ? { submitBlockers } : {})}
          motive={composition.motive}
        />
      }
    >
      {composition.summary}
    </CheckoutTripSummary>
  );
}
